import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, Lightbulb, ListOrdered } from 'lucide-react'
import { MermaidDiagram, SidePanel, Button } from '@/shared/ui'
import type {
  HelpContent,
  HelpDiagram,
  HelpGuide,
  HelpPage,
  HelpTipSection,
} from '@/core/help/help-registry'
import { isTypingIn } from '@/shared/utils/keyboard'

/**
 * Per-screen help context — a string id describing the sub-view the user
 * is currently looking at. When set, `<HelpPanel>` looks for a matching
 * `HelpPage.id` and renders only that page (no tab strip).
 *
 * Default source is the URL `?tab=` query param (set by `<AppShell>`),
 * so any page that already uses `?tab=` for state gets context-aware help
 * for free. Components without URL state (e.g. an internal stepper inside
 * a modal) can override locally by wrapping their subtree in
 * `<HelpContextOverride id="..."/>`.
 */
const HelpContext = createContext<string | null>(null)

export function HelpContextProvider({
  id,
  children,
}: {
  id: string | null
  children: ReactNode
}) {
  return <HelpContext.Provider value={id}>{children}</HelpContext.Provider>
}

/**
 * Override the active help context for a subtree. Useful when a component
 * has its own internal state that doesn't live in the URL — e.g. a
 * multi-step modal whose current step should drive which help page shows.
 */
export function HelpContextOverride({
  id,
  children,
}: {
  id: string | null
  children: ReactNode
}) {
  const parent = useContext(HelpContext)
  return (
    <HelpContext.Provider value={id ?? parent}>{children}</HelpContext.Provider>
  )
}

export function useHelpContextId(): string | null {
  return useContext(HelpContext)
}

/**
 * Global `?` shortcut — opens the contextual help panel for the current
 * screen. Suppressed while typing in an input / textarea / contenteditable
 * (single-key shortcut rule from `docs/ui-ux-patterns.md §10`). No-ops when
 * there is no helpContent for the current route (the caller passes null).
 */
export function useHelpShortcut(openHelp: (() => void) | null) {
  useEffect(() => {
    if (!openHelp) return
    const onKey = (e: KeyboardEvent) => {
      // `?` is typically Shift+/ on US/UK keyboards; keep both paths.
      if (e.key !== '?') return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (isTypingIn(e.target)) return
      e.preventDefault()
      openHelp()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [openHelp])
}

interface Props {
  content: HelpContent
  onClose: () => void
  /**
   * Optional override that bypasses the `HelpContext` provider chain.
   * Mostly useful for tests; production code wires the active id through
   * `<HelpContextProvider>` higher up the tree.
   */
  activePageId?: string | null
}

/**
 * Renders a HelpContent entry inside a SidePanel. Three rendering paths:
 *
 *   1. `pages` array + matching `activePageId` → render only that page
 *      (no tab strip). Mirrors what the user is looking at on the screen.
 *   2. `pages` array + no match → render with the tab strip so the user
 *      can browse the multi-section content.
 *   3. Flat content (no `pages`) → render description + tips + guides.
 *
 * Tips and guide steps may contain `**phrase**` markers. These are parsed
 * by `renderInline` and wrapped in <strong> for visual emphasis.
 */
export function HelpPanel({ content, onClose, activePageId }: Props) {
  const ctxId = useContext(HelpContext)
  // Prop wins over context so tests can pin a value; falls back to context
  // (set by `<HelpContextProvider>` near the app root) for production.
  const effectiveId = activePageId !== undefined ? activePageId : ctxId
  const matchedPage = useMemo(() => {
    if (!effectiveId || !content.pages) return null
    return content.pages.find((p) => p.id === effectiveId) ?? null
  }, [effectiveId, content.pages])

  return (
    <SidePanel onClose={onClose} title={content.title} width={420}>
      {matchedPage ? (
        <HelpBody
          description={matchedPage.description}
          tips={matchedPage.tips}
          sections={matchedPage.sections}
          guides={matchedPage.guides}
          diagrams={matchedPage.diagrams}
          onNavigate={onClose}
        />
      ) : content.pages && content.pages.length > 0 ? (
        <HelpPages pages={content.pages} onNavigate={onClose} />
      ) : (
        <HelpBody
          description={content.description}
          tips={content.tips}
          sections={content.sections}
          guides={content.guides}
          diagrams={content.diagrams}
          onNavigate={onClose}
        />
      )}
    </SidePanel>
  )
}

/**
 * Parse a string with `**phrase**` bold markers into React nodes.
 * Matches are wrapped in <strong> with the Night Ops accent (light blue)
 * so key actions / labels pop against the body text. Plain segments are
 * wrapped in <span> to give every part a stable key.
 */
function renderInline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong
        key={i}
        style={{ color: 'var(--accent)', fontWeight: 'var(--fw-semibold)' }}
      >
        {part.slice(2, -2)}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  )
}

function HelpPages({ pages, onNavigate }: { pages: HelpPage[]; onNavigate: () => void }) {
  const [active, setActive] = useState(0)
  const page = pages[active] ?? pages[0]
  if (!page) return null
  return (
    <div className="help-pages">
      <div className="help-tabs" role="tablist">
        {pages.map((p, i) => (
          <button
            key={p.label}
            type="button"
            role="tab"
            aria-selected={i === active}
            className={`help-tab ${i === active ? 'active' : ''}`.trim()}
            onClick={() => setActive(i)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <HelpBody
        description={page.description}
        tips={page.tips}
        sections={page.sections}
        guides={page.guides}
        diagrams={page.diagrams}
        onNavigate={onNavigate}
      />
    </div>
  )
}

function HelpBody({
  description,
  tips,
  sections,
  guides,
  diagrams,
  onNavigate,
}: {
  description?: string | undefined
  tips?: string[] | undefined
  sections?: HelpTipSection[] | undefined
  guides?: HelpGuide[] | undefined
  diagrams?: HelpDiagram[] | undefined
  onNavigate: () => void
}) {
  return (
    <div className="help-body">
      {description && (
        <p className="help-description">{renderInline(description)}</p>
      )}
      {sections && sections.length > 0 && <HelpTipSections sections={sections} />}
      {tips && tips.length > 0 && (
        <section className="help-section">
          <h3>Consejos</h3>
          <ul className="help-tips">
            {tips.map((tip, i) => (
              <li key={i}>{renderInline(tip)}</li>
            ))}
          </ul>
        </section>
      )}
      {guides && guides.length > 0 && (
        <section className="help-section">
          <h3>Guías</h3>
          <div className="help-guides">
            {guides.map((guide, i) => (
              <HelpGuideItem key={i} guide={guide} onNavigate={onNavigate} />
            ))}
          </div>
        </section>
      )}
      {diagrams && diagrams.length > 0 && (
        <section className="help-section">
          <h3>Diagramas</h3>
          <div className="help-diagrams">
            {diagrams.map((d, i) => (
              <MermaidDiagram
                key={i}
                source={d.source}
                {...(d.caption ? { caption: d.caption } : {})}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

/**
 * Collapsible tip sections rendered as a single-expand accordion: all
 * collapsed at first (the panel reads as a scannable index of section
 * headers), and opening one closes the previous — same "one open at a
 * time" model as `ConfigPanelGroup singleExpand`. Expand/collapse uses the
 * shared `.collapsible` utility so it animates identically to the sidebar
 * accordions. Styled as `.help-guide`-style cards for a consistent panel.
 */
function HelpTipSections({ sections }: { sections: HelpTipSection[] }) {
  // All sections collapsed at first — the panel opens as a scannable index
  // of section headers; the user expands the one they need (single-expand).
  const [openIndex, setOpenIndex] = useState(-1)
  return (
    <div className="help-sections">
      {sections.map((section, i) => {
        const open = i === openIndex
        return (
          <section
            className={`help-section-card ${open ? 'is-open' : ''}`.trim()}
            key={i}
          >
            <h3 className="help-section-heading">
              <button
                type="button"
                className="help-section-toggle"
                aria-expanded={open}
                onClick={() => setOpenIndex(open ? -1 : i)}
              >
                <Lightbulb size={14} className="help-row-ico" aria-hidden="true" />
                <span className="help-row-title">{section.label}</span>
                <span className="help-row-count">{section.tips.length}</span>
                <ChevronDown
                  size={14}
                  className="help-section-chev"
                  aria-hidden="true"
                />
              </button>
            </h3>
            <div
              className={`collapsible ${open ? 'collapsible--open' : ''}`.trim()}
              inert={!open}
              aria-hidden={!open}
            >
              <div className="collapsible-inner">
                <ul className="help-tips">
                  {section.tips.map((tip, j) => (
                    <li key={j}>{renderInline(tip)}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )
      })}
    </div>
  )
}

function HelpGuideItem({ guide, onNavigate }: { guide: HelpGuide; onNavigate: () => void }) {
  const navigate = useNavigate()
  // Controlled collapse so close also animates (native <details> hides
  // children via `display: none`, which kills the transition). State
  // starts closed — guides read as a scannable index; user opens the
  // one they need.
  const [open, setOpen] = useState(false)
  const handleCta = () => {
    if (guide.screenTarget) navigate(guide.screenTarget)
    onNavigate()
  }
  return (
    <article
      className={`help-guide ${open ? 'is-open' : ''}`.trim()}
    >
      <button
        type="button"
        className="help-guide-summary"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <ListOrdered
          size={14}
          className="help-row-ico help-row-ico--guide"
          aria-hidden="true"
        />
        <span className="help-guide-title">{guide.title}</span>
        <span className="help-row-count">{guide.steps.length}</span>
        <ChevronDown
          size={14}
          className="help-guide-chev"
          aria-hidden="true"
        />
      </button>
      <div
        className={`collapsible ${open ? 'collapsible--open' : ''}`.trim()}
        inert={!open}
        aria-hidden={!open}
      >
        <div className="collapsible-inner">
          <div className="help-guide-body">
            <ol>
              {guide.steps.map((step, i) => (
                <li key={i}>{renderInline(step)}</li>
              ))}
            </ol>
            {guide.screenTarget && (
              <Button variant="outline" size="sm" onClick={handleCta}>
                {guide.ctaLabel ?? 'Ir allí'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </article>
  )
}
